import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Box, Typography, TextField, CardContent, Grid, Drawer, Stack, Avatar, CircularProgress, Tooltip, SelectChangeEvent } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import CustomTextField from "../FormFields/CustomTextField";
import EstimateDeliveryTableInlineEdit from '../Table/TableInlineEdit2';
import DialogClientForm from '../Dialog/CreateClientForm';
import DialogDocumentSettingForm from '../DialogDocumentSettingForm/DialogDocumentSettingForm';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledCard } from '../AccountBasicInfo/AccountBacisInfo.styles';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchEstimateSeqNumber, addEstimate, fetchEstimateById, updateEstimate, dispatchSale } from '../../store/estimateStore/estimateThunk';

import { fetchAccountById, fetchCounterPartyAccountById } from "../../store/accountThunk";
import { fetchDocumentSettings } from '../../store/documentSettingStore/documentSettingThunk';
import { fetchAccountsByName } from "../../store/accountThunk";
import { EstimateFormInputs } from "../../utils/estimate.types";
import DocAttachInfo from '../DocumentAttachment/DocAttachInfo';
import PayTo from './PayTo';
import EstiamteButtons from './EstimateFooterButtons';
import TemplateSelect from './TemplateSelect'
import { StyledBoxLoader } from './Invoice.style';
import { Utils } from "../../utils/common-utils";
import moment from 'moment';

import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import { fetchStates } from '../../store/stateStore/stateThunk';

// Schema to validate account form.
const schema = yup.object().shape({
  estimateNumber: yup.string().required("Estimate Number is required"),
  client: yup.string().required("Please select Client"),
  account: yup.string().required("Account is required."),
  lineItem: yup.string().required("Minimum 1 Line Item required.")
});

interface EstimateProps {
  type: string;
}

// Estimate component. 
const Estimate: FC<EstimateProps> = ({ type }) => {
  const { pathname } = useLocation();
  const { estimateId } = useParams();
  const isEditing = estimateId ? true : false;

  // use form
  const methods = useForm<EstimateFormInputs>({
    resolver: yupResolver(schema),
  });

  // States
  const [open, setOpen] = useState<Array<boolean>>([false, false, false, false]);
  const [estimateDate, setEstimateDate] = useState<Date>(new Date());
  const [createDate, setCreateDate] = useState<Date>(new Date());
  const [estimateExpiryDate, setEstimateExpiryDate] = useState<Date>(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000));
  const [poDate, setPoDate] = useState<Date>();
  const [invoiceSeqData, setInvoiceSeqData] = useState<any>();
  const estimateDetails = useAppSelector(state => state.estimates.estimateDetails);
  const { documentSettings } = useAppSelector(state => state.documentSettings);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { countries } = useAppSelector(state => state.country);
  const { countryStates } = useAppSelector(state => state.countryState);
  const { docs } = useAppSelector(state => state.docAttach);
  const [inLineItems, setInLineItems] = useState<any[]>([]);
  const [payToObj, setPayToObj] = useState<any>();
  const [editInLineItems, setEditInLineItems] = useState<any[]>([]);
  const [docData, setDocData] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>();
  const [selectedBuyer, setSelectedBuyer] = useState<any>();
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [isClientLoading, setIsClientLoading] = useState(false);
  const [isBuyerLoading, setIsBuyerLoading] = useState(false);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isformLoaded, setIsFormLoaded] = useState(false);
  const [fetchData, setFetchData] = useState('');
  const [fetchDocs, setFetchDocs] = useState('');
  const [docDeleteId, setDocDeleteId] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("DRAFT");
  const [payToAccount, setPayToAccount] = useState<any>();
  const formRef = useRef<any>();
  const [isDispatched, setIsDispatched] = useState(false);
  const [queryParams, setQueryParams] = useState('');
  const [documentSettingId, setDocumentSettingId] = useState('');
  const [newInvoiceId, setNewInvoiceId] = useState('');

  const [stateOptions, setStateOptions] = useState<IOptions[]>([]);
  const [selectedState, setSelectedState] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const MODULE_TYPE = "sale-estimate";

  const openCloseDrawer = (label: string) => {
    let openArray = [...open]
    if (label === 'document') {
      openArray[0] = !openArray[0];
    } else if (label === 'client') {
      openArray[1] = !openArray[1];
      setIsClientLoading(true);
    } else if (label === 'agent') {
      openArray[2] = !openArray[2];
      setIsAgentLoading(true);
    } else {
      openArray[3] = !openArray[3];
      setIsClientLoading(true);
    }
    setOpen(openArray);
  }

  const closeDialog = (isSaved: boolean) => {
    let openArray = [...open]
    openArray.forEach((x, i) => openArray[i] = false);
    setOpen(openArray);

    if (isSaved) {
      setFetchData(Math.random().toString());
    }
  }
  

  const closeDocAttach = (isSaved: boolean) => {
    if (isSaved) {
      setFetchDocs(Math.random().toString());
    }
  }

  const cancelDialog = () => {
    setIsClientLoading(false); setSelectedAgent(false); setIsAgentLoading(false); setIsLoading(false);
  }

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'estimateDate')
      setEstimateDate(newValue);
      else if (name === 'estimateExpiryDate')
      setEstimateExpiryDate(newValue);
      else
        setCreateDate(newValue);
    }
  };

  const rowOnChangeHandler = (data: any) => {
    setInLineItems(data);
  };

  const payToOnChangeHandler = (data: any) => {
    setPayToObj(data);
  };

  const selectedClientHandler = (data: any) => {
    setSelectedClient(data);
  };

  const selectedAgentHandler = (data: any) => {
    setSelectedAgent(data);
  };

  //Method to handle save and send click events
  const selectedDispatchHandler = async (data: any) => {
    console.log("selectedDispatchHandler called")
    let params = '';
    let currentValues = methods.getValues();
    console.log(currentValues);
    Object.entries(data).forEach(([key, value]) => {
      if (value)
        params += key + '=' + value + '&';
    });
    console.log(params);
    setQueryParams(params.slice(0, -1));
    methods.setValue('pdfTemplate', currentValues.pdfTemplate);
    console.log(formRef)
    if (formRef) {
      console.log("in form ref");
      setIsDispatched(true);
      console.log(formRef?.current)
      console.log(formRef?.current.submit)
      formRef?.current.submit.click();
      console.log("formRef?.current.submit.click() called");
    }
    // }
  };

  // Method to handle invoice form submit.
  const onSubmit = async (data: EstimateFormInputs) => {
    console.log("onsubmit called =========>");
    if (data) {
      console.log(data);
      delete data.lineItem;
      delete data.agent;
      delete data.amountFormat;

      data.lineItems = Object.values(inLineItems);

      data.date = estimateDate.toString();
      data.estimateExpiryDate = estimateExpiryDate.toString();
      data.createDate = estimateDate.toString();
      data.invoiceType = "Estimate"

      if (type === MODULE_TYPE) {
        data.supplier = selectedAccount._id;
        data.buyer = selectedClient._id;
        data.invoiceAccountRelation = 'AR';
      } else {
        data.supplier = selectedClient._id;
        data.buyer = selectedAccount._id;
        data.invoiceAccountRelation = 'AP';
      }
      data.account = selectedAccount._id;
      let address = (type === MODULE_TYPE) ? selectedClient.addresses : selectedBuyer.addresses;
      let shippingAddress = address.find((item: any) => item.type === 'Shipping');
      let billingAddress = address.find((item: any) => item.type === 'Billing');
      data.billingAddress = (billingAddress) ? billingAddress._id : (type === MODULE_TYPE) ? selectedClient._id : selectedBuyer._id;
      data.shippingAddress = (shippingAddress) ? shippingAddress._id : (type === MODULE_TYPE) ? selectedClient._id : selectedBuyer._id;
      data.client = selectedClient._id;
      if (selectedAgent) data.agent = selectedAgent._id;
    }

    try {
      if (!isEditing) {
        await dispatch(addEstimate(data)).unwrap().then(async (data) => {
          if (data) {
            let _estimateId = data._id
            if (_estimateId) setNewInvoiceId(_estimateId)
          }
        });
      } else {
        if (estimateId) {
          await dispatch(updateEstimate({ id: estimateId, estimatePayload: data })).unwrap()
          setNewInvoiceId(estimateId)
        }
      }
    } catch (error: any) {
      // Empty catch block. 
    }
  };

  //Method to handle Cancel button click.
  const onCancel = () => {
    methods.reset();
    const cancelLink = `/sales`;
    navigate(cancelLink);
  };

  const getEstimateDetails = async () => {
    await dispatch(fetchEstimateSeqNumber()).then(async (data: any) => {
      console.log(data);
      if (data.payload) {
        setIsFormLoaded(true);
        setIsLoading(true);
        setInvoiceSeqData(data.payload);
      } else
        methods.trigger("estimateNumber");
    }).catch(e => {
      console.log(e);
    });
  };

  const docDelete = (id: any) => {
    setDocDeleteId(id)
  }

  useEffect(() => {
    dispatch(fetchStates()).unwrap().then(async (data) => {
      console.log(data);
      if (data) {
        if (data.length > 0) {
          await data.forEach(async (item: string | any) => {
            setStateOptions((stateOptions: any) => ([...stateOptions, { label: item.name, value: item._id }]));
          });
        }
      }
    });
  }, []);



  // fetch current estimate details on edit
  useEffect(() => {
    console.log("estimate id found == " + estimateId);
    if (estimateId) {
      console.log("found 111 estimate id found == " + estimateId);
      dispatch(fetchEstimateById(estimateId));
    }
  }, [estimateId, dispatch]);


  useEffect(() => {
    if (docDeleteId) {
      let docs = docData.filter(doc => doc._id !== docDeleteId);
      setDocData([...docs]);
      setDocDeleteId('');
    }
  }, [docDeleteId])

  useEffect(() => {
    const navigateDispatch = async () => {
      if (newInvoiceId) {
        console.log("newInvoiceId true")
        if (isDispatched) {
          console.log("isDispatched true")
          let currentData = methods.getValues();
          await dispatch(dispatchSale(
            { saleId: newInvoiceId, saleType: type, params: queryParams, dispatchPayload: { templateId: currentData.pdfTemplate } }))
            .then(async (data: any) => { 
              if (data.payload) onCancel(); 
            });
        } else {
          if (type == "sale-estimate") {
            navigate(`/sales/estimate/edit/${newInvoiceId}`);
          } else {
            navigate(`/sales/delivery-note/edit/${newInvoiceId}`);
          }
        }
      }
    }
    navigateDispatch();
  }, [isDispatched, newInvoiceId, dispatch, methods])


  useEffect(() => {
    console.log("calling useEffect for setting address and client Name")
    console.log(selectedClient);

    if (selectedClient) {

      if (type === MODULE_TYPE) { setAddesses(selectedClient) }
      methods.setValue("client", selectedClient.name);
      setIsClientLoading(false);
    }

    if (selectedAgent) {
      methods.setValue("agent", selectedAgent.name);
      setIsAgentLoading(false);
    }
  }, [selectedClient, selectedAgent, methods, countries]);

  //Set edit estimate data in case of edit.
  useEffect(() => {
    const setInitialData = async () => {
      console.log("called setInitialData from main ");
      console.log(estimateDetails);
      console.log(isEditing);
      console.log("estimatedeails  setInitialData data printed ");
      try {
        if (estimateDetails && isEditing) {
          console.log("called setInitialData from isEditing ");
          console.log(estimateDetails);
          console.log(isEditing);
          console.log("estimatedeails  setInitialData data printed ");
          setIsFormLoaded(true);
          setIsClientLoading(true);
          setIsLoading(true);
          methods.setValue('estimateNumber', estimateDetails.estimateNumber);
          methods.setValue("account", selectedAccount.name);
          methods.setValue("terms", estimateDetails.terms);
          methods.setValue("footer1", estimateDetails.footer1);
          methods.setValue("poNumber", estimateDetails.poNumber);
          methods.setValue("poDate", estimateDetails.poDate);
          methods.setValue("paymentTerms", estimateDetails.paymentTerms);
          methods.setValue('pdfTemplate', estimateDetails.pdfTemplate);
          methods.setValue('estimateDate', estimateDetails.date);
          methods.setValue('estimateExpiryDate', estimateDetails.estimateExpiryDate);
          methods.setValue('estimateExpiryDate', estimateDetails.estimateExpiryDate);
          methods.setValue('placeOfSupply', estimateDetails.placeOfSupply);

          console.log('placeOfSupply obj: ', estimateDetails.placeOfSupply);

          // methods.setValue("placeOfSupply", estimateDetails.placeOfSupply?._id || '');
          setPlaceOfSupply(estimateDetails.placeOfSupply?._id || '');

          console.log('estimateDetails?.client?._id: ', estimateDetails?.client?._id);

          

      
          
          let selectedClient = await dispatch(fetchCounterPartyAccountById(estimateDetails?.client?._id));
          setSelectedClient(selectedClient.payload);

          setInLineItems(estimateDetails.lineItems)
          setEditInLineItems(estimateDetails.lineItems)

          setEstimateDate(estimateDetails.date)
          setEstimateExpiryDate(estimateDetails.estimateExpiryDate)

          setInvoiceStatus(estimateDetails.status)
          setIsLoading(false);
          // if (estimateDetails.status !== 'DRAFT') onCancel();
          setIsFormLoaded(false);
          setIsClientLoading(false);
          setIsLoading(false);
        }
      } catch (error: any) {
        // console.log("setInitialData catch block called");
      }
    };

    if (Object.keys(estimateDetails).length > 0) setInitialData();
  }, [methods, estimateDetails, dispatch, isEditing, selectedAccount, type]);


  useEffect(() => {
    console.log("useeffect called 1111");
    if (invoiceSeqData) {
      console.log("invoiceSeqdata is present")
      console.log(invoiceSeqData)
      if (type === MODULE_TYPE && !isEditing) {
        console.log(!isEditing);
        console.log("setting of estimateNumber");
        methods.setValue("estimateNumber", invoiceSeqData.estimateNumber);
        methods.setValue("terms", invoiceSeqData.termsAndConditions);
        methods.setValue("footer1", invoiceSeqData.footer1);
        setIsLoading(false)
      }
      setTimeout(() => setIsFormLoaded(false), 1000);
    }
  }, [invoiceSeqData, methods, type])

  useEffect(() => {
    console.log("useeffect called 99999")
    if (type === MODULE_TYPE) {
      if (documentSettings.length === 0 && isEditing)
        dispatch(fetchDocumentSettings(selectedAccount._id));

      if (documentSettings.length > 0) {
        let obj = documentSettings.find((o: any) => o.documentType.name.toLowerCase() === 'quotation');
        setDocumentSettingId(obj?._id || '');
      }
    }
  }, [selectedAccount, documentSettings, dispatch, type, isEditing])

  //Fetch current selected account .
  useEffect(() => {
    console.log("useeffect called if fetch data 8888 ");
    console.log(fetchData);
    if (fetchData)
      console.log("useeffect called from fetchdata ");
      getEstimateDetails();
  }, [dispatch, fetchData]);

  //Fetch all last estimate seq number and estimate details.
  useEffect(() => {
    console.log("useeffect called if  isEditing && 'account-receivable' 9999 ")
    if (!isEditing && type === MODULE_TYPE)
    console.log("calling getEstimateDetails from last ")
      getEstimateDetails();
  }, [isEditing]);

  const setAddesses = (object: any) => {
    methods.setValue("shippingAddress", '')
    methods.setValue("billingAddress", '')
    let address = object.addresses;
    let shippingAddress = address.find((item: any) => item.type === 'Shipping') || [];
    let billingAddress = address.find((item: any) => item.type === 'Billing') || [];
    if (shippingAddress) {
      let countryName = countries.find((item: any) => item._id === shippingAddress.country);
      let _shippingAdddress = [shippingAddress.city, shippingAddress.state, countryName?.name, shippingAddress.line1, shippingAddress.line2, shippingAddress.code];
      methods.setValue("shippingAddress", _shippingAdddress.filter(n => n).join(", "));
    }
    if (billingAddress) {
      let countryName = countries.find((item: any) => item._id === billingAddress.country);
      let _billingAddress = [billingAddress.city, billingAddress.state, countryName?.name, billingAddress.line1, billingAddress.line2, billingAddress.code];
      methods.setValue("billingAddress", _billingAddress.filter(n => n).join(", "));
    }
  };

  useEffect(() => {
    console.log("calling useEffect for setting address and client Name")
    console.log(selectedClient);

    if (selectedClient) {

      if (type === MODULE_TYPE) { setAddesses(selectedClient) }
      methods.setValue("client", selectedClient.name);
      setIsClientLoading(false);
    }

    if (selectedAgent) {
      methods.setValue("agent", selectedAgent.name);
      setIsAgentLoading(false);
    }

    console.log("place of supply value 9999999   " + placeOfSupply);
    console.log(placeOfSupply);

    if (placeOfSupply) {
      methods.setValue("placeOfSupply", placeOfSupply);
    }
  }, [selectedClient, placeOfSupply, selectedAgent, methods, countries]);

  useEffect(() => {
    if (!isEditing) {
      methods.setValue("account", selectedAccount.name);
      methods.setValue("createDate", createDate.toString());
      methods.setValue("paymentTerms", "Net-30");
      if (type === MODULE_TYPE)
        setPayToAccount(selectedAccount)
    }
  }, [methods, selectedAccount, createDate, isEditing, type]);

  useEffect(() => {
    console.log("updating InLineFields useeffect 10000000000")
    const updateInLineFeild = async () => {
      if (inLineItems.length > 0) {
        let initialSum = 0;
        let sumWithInitial = inLineItems.reduce((previousValue, currentValue) => previousValue + currentValue.total, initialSum);

        let taxAmount = 0;
        let tax1 = 0;
        let tax2 = 0;
        let tax3 = 0;
        inLineItems.forEach((item) => {
          taxAmount += item.amount;
          tax1 += item.sgsttotal;
          tax2 += item.cgsttotal;
          tax3 += item.igsttotal;
        })
        methods.setValue("lineItem", 'filled');
        methods.setValue("amount", sumWithInitial);
        methods.setValue("amountFormat", Utils.formatNumber(sumWithInitial, true));
      } else
        methods.setValue("lineItem", '');
    };

    updateInLineFeild();
  }, [inLineItems, methods]);

  useEffect(() => {
    if (docs.length > 0 && fetchDocs) {
      let len = docs.length;
      setDocData(d => [...d, docs[len - 1]]);
    }
  }, [docs, fetchDocs])

  useEffect(() => {
    if (type === 'account-payable') {
      setIsBuyerLoading(true);
      dispatch(fetchAccountsByName(selectedAccount.name)).unwrap().then((data) => {
        if (data) {
          data.accounts = data.accounts.filter(function (item) {
            return item._id == selectedAccount._id;
          });
          setSelectedBuyer(data.accounts[0]);
          setAddesses(data.accounts[0]);
          setIsBuyerLoading(false);
        }
      });
    }
  }, [selectedAccount])

  const handleChange = (event: SelectChangeEvent<any>) => {
    setSelectedState(event.target.value);

  };

  return (
    <StyledCard sx={{ pb: 5 }}>
      <FormProvider {...methods}>
        <CardContent className={'invoiceContent'}>
          <Typography className={'cardHeader'} variant="h4" component="div" style={{borderBottom: '1px solid black'}}>
            <Box>{isEditing ? "Edit Estimate" : "Create Estimate"}</Box>
            <Box style={{ fontWeight: 'normal', alignItems: 'center', display: 'flex', flexDirection: 'row' }}>{(type === MODULE_TYPE) && "Estimate Setup"}
              {(type === MODULE_TYPE) && <Tooltip title="Estimate Setup"><SettingsIcon style={{ marginLeft: '5px', cursor: 'pointer' }} onClick={() => { openCloseDrawer('document') }} fontSize="small" /></Tooltip>}</Box>
          </Typography>
          <Box component="form" onSubmit={methods.handleSubmit(data => onSubmit(data))} ref={formRef} noValidate sx={{ mt: 0 }} style={{ position: 'relative' }}>
            {isformLoaded && <StyledBoxLoader component="div" sx={{ mt: 1 }}><CircularProgress color="inherit" /></StyledBoxLoader>}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container style={{ justifyContent: 'space-between', padding: '20px 10px', backgroundColor: '' }}>
                <Grid item md={2} xs={12}>
                  {isLoading ? <div></div> : <CustomTextField variant="outlined" label="Estimate No." name="estimateNumber" required InputProps={{ readOnly: (type === MODULE_TYPE || (isEditing && type === MODULE_TYPE)) ? true : false, disabled: (type === MODULE_TYPE) ? true : false }} className={(type === MODULE_TYPE) ? 'disabledField' : ''} />}
                </Grid>
                
                <Grid item md={5} xs={12}>
                  {(type === MODULE_TYPE) && <div style={{ display: 'flex' }}>
                    <Avatar
                      alt={selectedAccount.name || 'Picture'}
                      imgProps={{ "referrerPolicy": 'no-referrer' }}
                      src={selectedAccount.logo}
                      variant='square'
                      style={{ width: '80px', height: '80px', objectFit: 'contain', marginRight: '10px' }}
                    />
                    {isLoading ? <div></div> : <CustomTextField className={'disabledField'} label="Supplier" variant="outlined" name="account" InputProps={{ readOnly: true, disabled: true }} />}
                  </div>}
                </Grid>
                <Grid item md={5} xs={12}>
                  {(type === MODULE_TYPE) && <>
                    <Stack style={{ flexDirection: 'row', alignItems: 'center', marginTop: '0px' }}>
                      <Avatar
                        alt={selectedClient?.name || ''}
                        imgProps={{ "referrerPolicy": 'no-referrer' }}
                        src={selectedClient?.logo || ''}
                        variant='square'
                        style={{ width: '80px', height: '80px', objectFit: 'contain', marginRight: '10px' }}
                      />
                      {isClientLoading ? <div></div> : <CustomTextField className={'disabledField'} variant="outlined" label={"Buyer"} name="client" required placeholder={'Needs to be selected from drawer'} InputProps={{ readOnly: true, disabled: true }} />}

                      <Tooltip title="Add Buyer"><SettingsIcon style={{ cursor: 'pointer' }} onClick={() => { openCloseDrawer('buyer') }} fontSize="medium" /></Tooltip>
                    </Stack>
                  </>}
                </Grid>
                <Grid item md={4} xs={12}>
                  <Stack style={{ marginTop: '16px', marginBottom: '8px', flexDirection: 'row', alignItems: 'center' }}>
                    <DesktopDatePicker
                      label="Estimate Date"
                      inputFormat="dd/MM/yyyy"
                      maxDate={moment().add(1).toDate()}
                      value={estimateDate}
                      onChange={(value) => { handleDateChange(value, 'estimateDate') }}
                      renderInput={(params) => <TextField {...params} name={'estimateDate'} required variant="outlined" style={{ marginRight: (type === 'account-receivable') ? '10px' : '0px', flex: 1 }} />}
                    />
                  </Stack>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Stack style={{ marginTop: '16px', marginBottom: '8px', flexDirection: 'row', alignItems: 'center' }}>
                    <DesktopDatePicker
                      label="Expiration Date"
                      inputFormat="dd/MM/yyyy"
                      value={estimateExpiryDate}
                      onChange={(value) => { handleDateChange(value, 'estimateExpiryDate') }}
                      renderInput={(params) => <TextField {...params} name={'estimateExpiryDate'} required variant="outlined" style={{ marginRight: (type === MODULE_TYPE) ? '10px' : '0px', flex: 1 }} />}
                    />
                    
                  </Stack>
                  <Typography style={{ color: '#9f9f9fc4' }} variant="body1">{'Default Expiry date is set to 15 days'}</Typography>
                </Grid>
                <Grid item md={4} xs={12}>
                  {stateOptions && <CustomSelect
                    name="placeOfSupply"
                    label="Select Place of Supply"
                    id="state"
                    variant="outlined"
                    options={stateOptions}
                    // value={selectedState}
                    value={placeOfSupply ?? ''}
                    placeholder="Select Place of Supply"
                    // onChange={(event: SelectChangeEvent<any>) => { handleChange(event) }} 

                    onChange={(event: SelectChangeEvent<any>) => {
                      methods.setValue("placeOfSupply", event.target.value);
                      setPlaceOfSupply(event.target.value);
                    }}
                    />
                  }
                </Grid>

                <Grid item md={4} xs={12}>
                  {isClientLoading || isBuyerLoading ? <div></div> : <CustomTextField className={'disabledField'} style={{ marginTop: '24px' }} variant="outlined" multiline label={"Bill To"} name="billingAddress" required InputProps={{ readOnly: true, disabled: true }} />}
                </Grid>
                <Grid item md={4} xs={12}>
                  {isClientLoading || isBuyerLoading ? <div></div> : <CustomTextField className={'disabledField'} style={{ marginTop: '24px' }} variant="outlined" multiline label={"Ship To"} name="shippingAddress" required InputProps={{ readOnly: true, disabled: true }} />}
                </Grid>  
                <Grid item md={4} xs={12}>
                  <TemplateSelect invoiceId={estimateId} invoiceStatus={invoiceStatus} invoiceDocType={'sale-estimate'} />
                </Grid> 
              </Grid>
              {/* <TemplateSelect invoiceId={estimateId} invoiceStatus={invoiceStatus} invoiceDocType={'sale-estimate'} /> */}

              <EstimateDeliveryTableInlineEdit onChangeRow={rowOnChangeHandler} rowsData={editInLineItems} />

              {/* <Typography>some text goess here {JSON.stringify(stateOptions)}</Typography> */}

              <Grid container sx={{ mt: 0 }} style={{ justifyContent: 'space-between', padding: '0px 6px' }}>
                <Grid item md={12} xs={12}><CustomTextField className={'hiddenText'} variant="standard" label="" name="lineItem" required /></Grid>

                <Grid item xs={12}>
                  <Grid item xs={12} md={12} style={{float: 'right'}}>
                    <CustomTextField className={'disabledField text-right'} label="Amount" variant="standard" name="taxAmount" InputProps={{ readOnly: true, disabled: true, size:'small' }} />
                    <CustomTextField className={`disabledField text-right`} label="Total" variant="standard" name="amountFormat" required InputProps={{ readOnly: true, disabled: true }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="amount" />
                  </Grid>
                </Grid>

                {(type === MODULE_TYPE) && <>
                  <Grid item md={12} xs={12}>
                    {isLoading ? <div></div> : <CustomTextField variant="outlined" multiline label="Terms & Conditions" name="terms" />}
                  </Grid>

                  <Grid item md={12} xs={12}>
                    {isLoading ? <div></div> : <CustomTextField variant="outlined" multiline label="Footer" name="footer1" InputProps={{ readOnly: true, disabled: true }} className={'disabledField'} />}
                  </Grid>
                </>}
              </Grid>
            </LocalizationProvider>

            <EstiamteButtons type={type} selectedSend={selectedDispatchHandler} invoiceStatus={invoiceStatus} onCancel={onCancel} />
          </Box>
        </CardContent>
      </FormProvider>

      <Drawer anchor="right" open={open[0]}>
        {<DialogDocumentSettingForm open={open[0]} setDocumentType={'invoice'} documentSettingId={documentSettingId} accountId={selectedAccount._id} onClose={closeDialog}></DialogDocumentSettingForm>}
      </Drawer>
      <Drawer anchor="right" open={open[3]} >
        {<DialogClientForm open={open[3]} title={'Buyer'} onClose={closeDialog} onCancel={cancelDialog} selectedData={selectedClientHandler}></DialogClientForm>}
      </Drawer>
    </StyledCard>
  );
}

export default Estimate;